// client/src/pages/bank-statements/Import.js

import React, { useState, useEffect } from 'react';
import Form from 'react-bootstrap/Form';
import Button from 'react-bootstrap/Button';
import Alert from 'react-bootstrap/Alert';
import Col from 'react-bootstrap/Col';
import Row from 'react-bootstrap/Row';
import Spinner from 'react-bootstrap/Spinner';
import useTransactions from '../../hooks/useTransactions';
import useCategories from '../../hooks/useCategories';
import useImportProfiles from '../../hooks/useImportProfiles';

const REQUIRED_FIELDS = [
  { key: 'date', label: 'Date de Transaction' },
  { key: 'description', label: 'Description' },
  { key: 'amount', label: 'Montant' },
  { key: 'runningBalance', label: 'Solde Courant (Optionnel)' },
  { key: 'merchant', label: 'Marchand (Optionnel)' },
  { key: 'currency', label: 'Devise (Optionnel)' }
];

function Import() {
  const { accounts, loading, fetchAccounts, analyzeCSV, importTransactions } = useTransactions();
  const { categorize, loading: catLoading } = useCategories();
  const { profiles, fetchProfiles, createProfile, deleteProfile } = useImportProfiles();

  const [selectedAccountId, setSelectedAccountId] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [separator, setSeparator] = useState(',');
  const [headers, setHeaders] = useState([]);
  const [mapping, setMapping] = useState({});
  const [selectedProfileId, setSelectedProfileId] = useState('');
  const [newProfileName, setNewProfileName] = useState('');
  const [message, setMessage] = useState('');
  const [variant, setVariant] = useState('info');

  const selectedProfile = profiles.find((p) => p.id === selectedProfileId) || null;
  const importing = loading.importTransactions || catLoading.categorize;

  useEffect(() => {
    fetchAccounts().catch(() => {
      setMessage('Impossible de charger la liste des comptes.');
      setVariant('danger');
    });
    fetchProfiles().catch(() => {});
  }, [fetchAccounts, fetchProfiles]);

  const resetMappingState = () => {
    setHeaders([]);
    setMapping({});
    setMessage('');
  };

  const handleFileChange = (event) => {
    setSelectedFile(event.target.files[0]);
    resetMappingState();
  };

  const handleProfileChange = (id) => {
    setSelectedProfileId(id);
    resetMappingState();
    const prof = profiles.find((p) => p.id === id);
    if (prof) setSeparator(prof.separator);
  };

  const handleDeleteProfile = async () => {
    if (!selectedProfile) return;
    if (!window.confirm(`Supprimer le profil « ${selectedProfile.name} » ?`)) return;
    try {
      await deleteProfile(selectedProfile.id);
      setSelectedProfileId('');
      await fetchProfiles();
    } catch (err) {
      setMessage(err.message);
      setVariant('danger');
    }
  };

  const handleAnalyze = async () => {
    if (!selectedFile) {
      setMessage("Veuillez sélectionner un fichier avant d'analyser.");
      setVariant('warning');
      return;
    }
    setMessage('');
    try {
      const result = await analyzeCSV(selectedFile, separator);
      setHeaders(result.headers);
      // Pré-remplit le mapping depuis le profil (colonnes réellement présentes).
      if (selectedProfile) {
        const seeded = {};
        Object.entries(selectedProfile.mapping).forEach(([key, header]) => {
          if (result.headers.includes(header)) seeded[key] = header;
        });
        setMapping(seeded);
      }
      setMessage('Analyse réussie ! Vérifiez la correspondance des colonnes.');
      setVariant('success');
    } catch (error) {
      setMessage(error.message);
      setVariant('danger');
    }
  };

  const handleMappingChange = (requiredKey, selectedHeader) => {
    setMapping((prev) => ({ ...prev, [requiredKey]: selectedHeader }));
  };

  const runImport = async (mappingToUse, separatorToUse) => {
    if (!selectedAccountId) {
      setMessage("Veuillez sélectionner un compte avant de lancer l'importation.");
      setVariant('warning');
      return;
    }
    if (!selectedFile) {
      setMessage('Veuillez sélectionner un fichier.');
      setVariant('warning');
      return;
    }
    setMessage('');
    try {
      const result = await importTransactions(
        selectedFile,
        mappingToUse,
        separatorToUse,
        selectedAccountId
      );
      resetMappingState();
      setVariant('success');
      setMessage(`${result.message} Catégorisation en cours…`);

      try {
        const stats = await categorize(selectedAccountId);
        const done = stats.viaCache + stats.viaAi;
        setMessage(
          `${result.message} ${done} transaction(s) catégorisée(s) automatiquement` +
            (stats.uncategorized > 0 ? ` (${stats.uncategorized} à revoir).` : '.')
        );
      } catch (catErr) {
        setMessage(
          `${result.message} La catégorisation automatique a échoué (${catErr.message}). ` +
            'Vous pouvez la relancer depuis la page Catégories.'
        );
        setVariant('warning');
      }
    } catch (error) {
      setMessage(error.message);
      setVariant('danger');
    }
  };

  const handleSaveProfile = async () => {
    const name = newProfileName.trim();
    if (!name) return;
    try {
      await createProfile({ name, separator, mapping });
      await fetchProfiles();
      setNewProfileName('');
      setMessage(`Profil « ${name} » enregistré. Il sera réutilisable au prochain import.`);
      setVariant('success');
    } catch (err) {
      setMessage(err.message);
      setVariant('danger');
    }
  };

  return (
    <div>
      <h1>Importer un Relevé Bancaire</h1>

      {headers.length === 0 && (
        <>
          <p>Associez un fichier CSV à un de vos comptes existants.</p>
          <Form>
            <Form.Group as={Row} className="mb-3" controlId="profileSelect">
              <Form.Label column sm="4">
                Profil d&apos;import
              </Form.Label>
              <Col sm="8" className="d-flex gap-2">
                <Form.Select
                  value={selectedProfileId}
                  onChange={(e) => handleProfileChange(e.target.value)}
                >
                  <option value="">-- Aucun (mapping manuel) --</option>
                  {profiles.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
                </Form.Select>
                {selectedProfile && (
                  <Button
                    variant="outline-danger"
                    className="flex-shrink-0"
                    onClick={handleDeleteProfile}
                  >
                    Supprimer
                  </Button>
                )}
              </Col>
            </Form.Group>

            <Form.Group as={Row} className="mb-3" controlId="accountSelect">
              <Form.Label column sm="4">
                Compte Bancaire
              </Form.Label>
              <Col sm="8">
                <Form.Select
                  value={selectedAccountId}
                  onChange={(e) => setSelectedAccountId(e.target.value)}
                  disabled={loading.accounts}
                >
                  <option value="">-- Choisissez un compte --</option>
                  {accounts.map((acc) => (
                    <option key={acc.id} value={acc.id}>
                      {acc.name} ({acc.iban})
                    </option>
                  ))}
                </Form.Select>
              </Col>
            </Form.Group>

            <Form.Group controlId="formFile" className="mb-3">
              <Form.Label>Fichier du relevé (.csv)</Form.Label>
              <Form.Control type="file" accept=".csv" onChange={handleFileChange} />
            </Form.Group>

            {!selectedProfile && (
              <Form.Group controlId="formSeparator" className="mb-3">
                <Form.Label>Séparateur de colonnes</Form.Label>
                <div>
                  <Form.Check
                    inline
                    type="radio"
                    label="Point-virgule (;)"
                    name="separator"
                    id="semicolon"
                    value=";"
                    checked={separator === ';'}
                    onChange={(e) => setSeparator(e.target.value)}
                  />
                  <Form.Check
                    inline
                    type="radio"
                    label="Virgule (,)"
                    name="separator"
                    id="comma"
                    value=","
                    checked={separator === ','}
                    onChange={(e) => setSeparator(e.target.value)}
                  />
                </div>
              </Form.Group>
            )}

            {selectedProfile ? (
              <div className="d-flex gap-2 flex-wrap">
                <Button
                  variant="success"
                  onClick={() => runImport(selectedProfile.mapping, selectedProfile.separator)}
                  disabled={importing || !selectedFile}
                >
                  {importing ? (
                    <Spinner as="span" animation="border" size="sm" />
                  ) : (
                    `Importer avec « ${selectedProfile.name} »`
                  )}
                </Button>
                <Button
                  variant="outline-secondary"
                  onClick={handleAnalyze}
                  disabled={loading.analyzeCSV || !selectedFile}
                >
                  Ajuster le mapping
                </Button>
              </div>
            ) : (
              <Button
                variant="primary"
                onClick={handleAnalyze}
                disabled={loading.analyzeCSV || !selectedFile}
              >
                {loading.analyzeCSV ? (
                  <Spinner as="span" animation="border" size="sm" />
                ) : (
                  '1. Analyser le fichier'
                )}
              </Button>
            )}
          </Form>
        </>
      )}

      {message && (
        <Alert variant={variant} className="mt-3">
          {message}
        </Alert>
      )}

      {headers.length > 0 && (
        <div className="mt-4">
          <h2>Étape 2/2 : Faites correspondre les colonnes</h2>
          <p>Associez les colonnes de votre fichier aux champs requis par l&apos;application.</p>
          {REQUIRED_FIELDS.map((field) => (
            <Form.Group as={Row} className="mb-3" controlId={field.key} key={field.key}>
              <Form.Label column sm="4">
                {field.label}
              </Form.Label>
              <Col sm="8">
                <Form.Select
                  aria-label={`Sélectionner la colonne pour ${field.label}`}
                  value={mapping[field.key] || ''}
                  onChange={(e) => handleMappingChange(field.key, e.target.value)}
                >
                  <option value="">-- Ne pas importer --</option>
                  {headers.map((header) => (
                    <option key={header} value={header}>
                      {header}
                    </option>
                  ))}
                </Form.Select>
              </Col>
            </Form.Group>
          ))}

          <Button
            variant="success"
            onClick={() => runImport(mapping, separator)}
            disabled={importing}
          >
            {importing ? (
              <Spinner as="span" animation="border" size="sm" />
            ) : (
              "2. Lancer l'importation"
            )}
          </Button>

          <hr className="my-4" />
          <Form.Group as={Row} controlId="saveProfile">
            <Form.Label column sm="4">
              Enregistrer comme profil
            </Form.Label>
            <Col sm="8" className="d-flex gap-2">
              <Form.Control
                placeholder="Nom du profil (ex : Boursorama)"
                value={newProfileName}
                onChange={(e) => setNewProfileName(e.target.value)}
              />
              <Button
                variant="outline-primary"
                className="flex-shrink-0"
                onClick={handleSaveProfile}
                disabled={!newProfileName.trim()}
              >
                Enregistrer
              </Button>
            </Col>
          </Form.Group>
        </div>
      )}
    </div>
  );
}

export default Import;
